import { AudioManager } from '../systems/AudioManager';

export class PauseMenu {
  private container: HTMLElement;
  private menuPanel: HTMLElement;
  private settingsPanel: HTMLElement;
  private isPaused: boolean = false;
  private audio: AudioManager;

  // 回调函数
  private onResumeCallback?: () => void;
  private onRestartCallback?: () => void;
  private onQuitCallback?: () => void;
  private onLoadCallback?: (slot: number) => boolean;

  constructor(audio: AudioManager) {
    this.audio = audio;

    // 创建主容器
    this.container = document.createElement('div');
    this.container.id = 'pause-menu-container';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: none;
      justify-content: center;
      align-items: center;
      z-index: 2000;
      backdrop-filter: blur(5px);
    `;

    // 创建菜单面板
    this.menuPanel = this.createMenuPanel();

    // 创建设置面板
    this.settingsPanel = this.createSettingsPanel();

    // 组装
    this.container.appendChild(this.menuPanel);
    this.container.appendChild(this.settingsPanel);
    document.body.appendChild(this.container);

    // 监听 ESC 键
    this.setupKeyboardListeners();
  }

  private createMenuPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.id = 'pause-menu-panel';
    panel.style.cssText = `
      background: rgba(20, 20, 20, 0.95);
      border: 3px solid #00ff00;
      border-radius: 15px;
      padding: 40px 60px;
      box-shadow: 0 8px 32px rgba(0, 255, 0, 0.4);
      min-width: 400px;
      display: block;
    `;

    // 标题
    const title = document.createElement('h1');
    title.textContent = '游戏暂停';
    title.style.cssText = `
      text-align: center;
      color: #00ff00;
      font-size: 48px;
      margin: 0 0 40px 0;
      text-shadow: 0 0 20px rgba(0, 255, 0, 0.8);
      font-family: Arial, sans-serif;
      font-weight: bold;
    `;

    // 按钮容器
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 15px;
    `;

    // 创建按钮
    const resumeBtn = this.createButton('继续游戏', '🎮', () => {
      this.resume();
    });

    const loadBtn = this.createButton('加载存档', '📂', () => {
      this.showSaveLoadPanel();
    });

    const restartBtn = this.createButton('重新开始', '🔄', () => {
      this.restart();
    });

    const settingsBtn = this.createButton('设置', '⚙️', () => {
      this.showSettings();
    });

    const quitBtn = this.createButton('退出游戏', '🚪', () => {
      this.quit();
    });

    // 组装按钮
    buttonsContainer.appendChild(resumeBtn);
    buttonsContainer.appendChild(loadBtn);
    buttonsContainer.appendChild(restartBtn);
    buttonsContainer.appendChild(settingsBtn);
    buttonsContainer.appendChild(quitBtn);

    // 自动保存提示
    const autoSaveHint = document.createElement('div');
    autoSaveHint.textContent = '💾 游戏会在完成关卡时自动保存';
    autoSaveHint.style.cssText = `
      text-align: center;
      color: #00ff00;
      font-size: 12px;
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid rgba(0, 255, 0, 0.3);
    `;
    buttonsContainer.appendChild(autoSaveHint);

    // 提示文字
    const hint = document.createElement('div');
    hint.textContent = '按 ESC 键继续';
    hint.style.cssText = `
      text-align: center;
      color: #888;
      font-size: 14px;
      margin-top: 30px;
    `;

    // 组装面板
    panel.appendChild(title);
    panel.appendChild(buttonsContainer);
    panel.appendChild(hint);

    return panel;
  }

  private createButton(text: string, icon: string, onClick: () => void): HTMLElement {
    const button = document.createElement('button');
    button.innerHTML = `<span style="margin-right: 10px;">${icon}</span>${text}`;
    button.style.cssText = `
      padding: 15px 30px;
      font-size: 20px;
      background: rgba(0, 255, 0, 0.1);
      border: 2px solid #00ff00;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.3s;
      font-family: Arial, sans-serif;
      font-weight: bold;
    `;

    button.addEventListener('mouseenter', () => {
      button.style.background = 'rgba(0, 255, 0, 0.3)';
      button.style.transform = 'translateX(10px)';
      button.style.boxShadow = '0 4px 20px rgba(0, 255, 0, 0.4)';
      this.audio.playSound('ui_click', 0.3);
    });

    button.addEventListener('mouseleave', () => {
      button.style.background = 'rgba(0, 255, 0, 0.1)';
      button.style.transform = 'translateX(0)';
      button.style.boxShadow = 'none';
    });

    button.addEventListener('click', () => {
      this.audio.playSound('ui_click');
      onClick();
    });

    return button;
  }

  private createSettingsPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.id = 'pause-settings-panel';
    panel.style.cssText = `
      background: rgba(20, 20, 20, 0.95);
      border: 3px solid #00ff00;
      border-radius: 15px;
      padding: 40px 60px;
      box-shadow: 0 8px 32px rgba(0, 255, 0, 0.4);
      min-width: 500px;
      display: none;
    `;

    // 标题
    const title = document.createElement('h2');
    title.textContent = '设置';
    title.style.cssText = `
      text-align: center;
      color: #00ff00;
      font-size: 36px;
      margin: 0 0 30px 0;
      text-shadow: 0 0 15px rgba(0, 255, 0, 0.8);
    `;

    // 音量控制区域
    const audioSection = document.createElement('div');
    audioSection.style.cssText = `
      margin-bottom: 30px;
    `;

    const audioTitle = document.createElement('h3');
    audioTitle.textContent = '音量控制';
    audioTitle.style.cssText = `
      color: white;
      font-size: 20px;
      margin-bottom: 20px;
      border-bottom: 2px solid rgba(0, 255, 0, 0.3);
      padding-bottom: 10px;
    `;

    audioSection.appendChild(audioTitle);

    // 创建音量滑块
    audioSection.appendChild(this.createVolumeSlider('主音量', 'master'));
    audioSection.appendChild(this.createVolumeSlider('音效', 'sfx'));
    audioSection.appendChild(this.createVolumeSlider('音乐', 'music'));

    // 图形设置区域（未来扩展）
    const graphicsSection = document.createElement('div');
    graphicsSection.style.cssText = `
      margin-bottom: 30px;
    `;

    const graphicsTitle = document.createElement('h3');
    graphicsTitle.textContent = '图形设置';
    graphicsTitle.style.cssText = `
      color: white;
      font-size: 20px;
      margin-bottom: 20px;
      border-bottom: 2px solid rgba(0, 255, 0, 0.3);
      padding-bottom: 10px;
    `;

    const qualityHint = document.createElement('div');
    qualityHint.textContent = '图形设置将在未来版本中添加';
    qualityHint.style.cssText = `
      color: #888;
      font-size: 14px;
      padding: 10px;
      text-align: center;
    `;

    graphicsSection.appendChild(graphicsTitle);
    graphicsSection.appendChild(qualityHint);

    // 返回按钮
    const backBtn = this.createButton('返回', '←', () => {
      this.hideSettings();
    });

    backBtn.style.marginTop = '20px';

    // 组装面板
    panel.appendChild(title);
    panel.appendChild(audioSection);
    panel.appendChild(graphicsSection);
    panel.appendChild(backBtn);

    return panel;
  }

  private createVolumeSlider(label: string, type: 'master' | 'sfx' | 'music'): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      margin-bottom: 20px;
    `;

    // 获取当前音量
    let currentVolume = 0.5;
    switch (type) {
      case 'master':
        currentVolume = this.audio.getMasterVolume();
        break;
      case 'sfx':
        currentVolume = this.audio.getSfxVolume();
        break;
      case 'music':
        currentVolume = this.audio.getMusicVolume();
        break;
    }

    // 标签行
    const labelRow = document.createElement('div');
    labelRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      color: white;
      font-size: 16px;
    `;

    const labelText = document.createElement('span');
    labelText.textContent = label;

    const valueText = document.createElement('span');
    valueText.textContent = `${Math.round(currentVolume * 100)}%`;
    valueText.style.color = '#00ff00';
    valueText.style.fontWeight = 'bold';

    labelRow.appendChild(labelText);
    labelRow.appendChild(valueText);

    // 滑块
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.value = String(Math.round(currentVolume * 100));
    slider.style.cssText = `
      width: 100%;
      height: 8px;
      border-radius: 4px;
      background: linear-gradient(to right,
        #00ff00 0%,
        #00ff00 ${currentVolume * 100}%,
        #333 ${currentVolume * 100}%,
        #333 100%
      );
      outline: none;
      cursor: pointer;
      -webkit-appearance: none;
    `;

    // 添加滑块样式
    const style = document.createElement('style');
    style.textContent = `
      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #00ff00;
        cursor: pointer;
        box-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
      }

      input[type="range"]::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #00ff00;
        cursor: pointer;
        border: none;
        box-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
      }
    `;
    document.head.appendChild(style);

    // 滑块事件
    slider.addEventListener('input', (e) => {
      const value = parseInt((e.target as HTMLInputElement).value);
      const normalizedValue = value / 100;

      valueText.textContent = `${value}%`;
      slider.style.background = `linear-gradient(to right,
        #00ff00 0%,
        #00ff00 ${value}%,
        #333 ${value}%,
        #333 100%
      )`;

      // 更新音量
      switch (type) {
        case 'master':
          this.audio.setMasterVolume(normalizedValue);
          break;
        case 'sfx':
          this.audio.setSfxVolume(normalizedValue);
          if (value > 0) {
            this.audio.playSound('ui_click', 0.5);
          }
          break;
        case 'music':
          this.audio.setMusicVolume(normalizedValue);
          break;
      }
    });

    wrapper.appendChild(labelRow);
    wrapper.appendChild(slider);

    return wrapper;
  }

  private setupKeyboardListeners(): void {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.settingsPanel.style.display === 'block') {
          // 如果在设置页面，返回主菜单
          this.hideSettings();
        } else if (this.isPaused) {
          // 如果已暂停，继续游戏
          this.resume();
        } else {
          // 否则暂停游戏
          this.pause();
        }
      }
    });
  }

  /**
   * 暂停游戏
   */
  pause(): void {
    if (this.isPaused) return;

    this.isPaused = true;
    this.show();

    // 暂停音乐
    this.audio.pauseMusic();

    // 触发暂停回调
    console.log('Game paused');
  }

  /**
   * 继续游戏
   */
  resume(): void {
    if (!this.isPaused) return;

    this.isPaused = false;
    this.hide();

    // 恢复音乐
    this.audio.resumeMusic();

    // 触发继续回调
    if (this.onResumeCallback) {
      this.onResumeCallback();
    }

    console.log('Game resumed');
  }

  /**
   * 重新开始游戏
   */
  restart(): void {
    // 确认对话框
    const confirmed = confirm('确定要重新开始游戏吗？当前进度将会丢失。');

    if (confirmed) {
      this.isPaused = false;
      this.hide();

      // 触发重新开始回调
      if (this.onRestartCallback) {
        this.onRestartCallback();
      }

      console.log('Game restarted');
    }
  }

  /**
   * 退出游戏
   */
  quit(): void {
    // 确认对话框
    const confirmed = confirm('确定要退出游戏吗？');

    if (confirmed) {
      this.isPaused = false;
      this.hide();

      // 触发退出回调
      if (this.onQuitCallback) {
        this.onQuitCallback();
      } else {
        // 默认行为：刷新页面
        window.location.reload();
      }

      console.log('Game quit');
    }
  }

  /**
   * 显示设置面板
   */
  showSettings(): void {
    this.menuPanel.style.display = 'none';
    this.settingsPanel.style.display = 'block';
    this.audio.playSound('ui_click');
  }

  /**
   * 隐藏设置面板
   */
  hideSettings(): void {
    this.menuPanel.style.display = 'block';
    this.settingsPanel.style.display = 'none';
    this.audio.playSound('ui_click');
  }

  /**
   * 显示暂停菜单
   */
  show(): void {
    this.container.style.display = 'flex';
    this.menuPanel.style.display = 'block';
    this.settingsPanel.style.display = 'none';
  }

  /**
   * 隐藏暂停菜单
   */
  hide(): void {
    this.container.style.display = 'none';
  }

  /**
   * 设置继续游戏的回调
   */
  onResume(callback: () => void): void {
    this.onResumeCallback = callback;
  }

  /**
   * 设置重新开始的回调
   */
  onRestart(callback: () => void): void {
    this.onRestartCallback = callback;
  }

  /**
   * 设置退出的回调
   */
  onQuit(callback: () => void): void {
    this.onQuitCallback = callback;
  }

  /**
   * 设置加载游戏的回调
   */
  onLoad(callback: (slot: number) => boolean): void {
    this.onLoadCallback = callback;
  }

  /**
   * 显示加载存档面板
   */
  private showSaveLoadPanel(): void {
    const title = '加载存档';
    const icon = '📂';

    // 创建模态对话框
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 3000;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background: rgba(20, 20, 20, 0.95);
      border: 3px solid #00ff00;
      border-radius: 15px;
      padding: 30px 40px;
      min-width: 400px;
      box-shadow: 0 8px 32px rgba(0, 255, 0, 0.4);
    `;

    // 标题
    const titleEl = document.createElement('h2');
    titleEl.innerHTML = `<span style="margin-right: 10px;">${icon}</span>${title}`;
    titleEl.style.cssText = `
      text-align: center;
      color: #00ff00;
      font-size: 28px;
      margin: 0 0 25px 0;
      text-shadow: 0 0 15px rgba(0, 255, 0, 0.8);
    `;
    panel.appendChild(titleEl);

    // 存档槽位
    const slotsContainer = document.createElement('div');
    slotsContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 20px;
    `;

    // 统一的关闭函数（需要在循环前定义）
    const closeModal = () => {
      if (modal.parentNode === document.body) {
        document.body.removeChild(modal);
      }
      document.removeEventListener('keydown', escHandler);
    };

    // ESC 关闭处理器
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    for (let i = 1; i <= 3; i++) {
      const slotBtn = document.createElement('button');
      slotBtn.innerHTML = `<span style="margin-right: 10px;">📁</span>存档 ${i}`;
      slotBtn.style.cssText = `
        padding: 15px 20px;
        font-size: 16px;
        background: rgba(0, 255, 0, 0.1);
        border: 2px solid #00ff00;
        border-radius: 8px;
        color: white;
        cursor: pointer;
        transition: all 0.3s;
        text-align: left;
      `;

      slotBtn.addEventListener('mouseenter', () => {
        slotBtn.style.background = 'rgba(0, 255, 0, 0.3)';
        slotBtn.style.transform = 'translateX(5px)';
        this.audio.playSound('ui_click', 0.3);
      });

      slotBtn.addEventListener('mouseleave', () => {
        slotBtn.style.background = 'rgba(0, 255, 0, 0.1)';
        slotBtn.style.transform = 'translateX(0)';
      });

      slotBtn.addEventListener('click', () => {
        this.audio.playSound('ui_click');

        if (this.onLoadCallback) {
          const success = this.onLoadCallback(i);
          if (success) {
            alert(`已加载存档 ${i}`);
            this.resume();
          } else {
            alert(`存档 ${i} 为空或加载失败`);
          }
        }

        closeModal();
      });

      slotsContainer.appendChild(slotBtn);
    }

    panel.appendChild(slotsContainer);

    // 返回按钮
    const backBtn = document.createElement('button');
    backBtn.innerHTML = '<span style="margin-right: 10px;">←</span>返回';
    backBtn.style.cssText = `
      width: 100%;
      padding: 12px 20px;
      font-size: 16px;
      background: rgba(100, 100, 100, 0.3);
      border: 2px solid #888;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.3s;
    `;

    backBtn.addEventListener('mouseenter', () => {
      backBtn.style.background = 'rgba(100, 100, 100, 0.5)';
      this.audio.playSound('ui_click', 0.3);
    });

    backBtn.addEventListener('mouseleave', () => {
      backBtn.style.background = 'rgba(100, 100, 100, 0.3)';
    });

    backBtn.addEventListener('click', () => {
      this.audio.playSound('ui_click');
      closeModal();
    });

    panel.appendChild(backBtn);
    modal.appendChild(panel);
    document.body.appendChild(modal);
    document.addEventListener('keydown', escHandler);
  }

  /**
   * 获取暂停状态
   */
  isPausedState(): boolean {
    return this.isPaused;
  }

  /**
   * 强制设置暂停状态（用于外部控制）
   */
  setPausedState(paused: boolean): void {
    this.isPaused = paused;
  }
}
